// Sound Effects API Service
import { getAuthHeaders } from "./auth";

// API_BASE_URL can be configured via environment variable
const API_BASE_URL = process.env.API_BASE_URL || "";

// Search sound effects
// Provider: Freesound (CC0 licensed sounds only)
// If no query is provided, backend uses a random soothing keyword
export async function searchSoundEffects(query, options = {}) {
  const { pageSize = 10, page = 1 } = options;
  
  const params = new URLSearchParams();
  if (query) params.append("query", query);
  if (pageSize) params.append("page_size", String(pageSize));
  if (page > 1) params.append("page", String(page));

  const response = await fetch(`${API_BASE_URL}/sound_effects/search?${params}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to search sound effects");
  }

  return response.json();
}

// Get sound effect categories
export async function getSoundEffectCategories() {
  const response = await fetch(`${API_BASE_URL}/sound_effects/categories`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to fetch categories");
  }

  return response.json();
}

// Get sound effect clips for a video
export async function getVideoSoundEffectClips(videoId) {
  const response = await fetch(`${API_BASE_URL}/videos/${videoId}/sound_effect_clips`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to fetch sound effect clips");
  }

  return response.json();
}

// Add sound effect clip to video
export async function addSoundEffectClip(videoId, clipData) {
  const response = await fetch(`${API_BASE_URL}/videos/${videoId}/sound_effect_clips`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(clipData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to add sound effect clip");
  }

  return response.json();
}

// Update sound effect clip
export async function updateSoundEffectClip(videoId, clipId, updates) {
  const response = await fetch(`${API_BASE_URL}/videos/${videoId}/sound_effect_clips/${clipId}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to update sound effect clip");
  }

  return response.json();
}

// Delete sound effect clip
export async function deleteSoundEffectClip(videoId, clipId) {
  const response = await fetch(`${API_BASE_URL}/videos/${videoId}/sound_effect_clips/${clipId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  if (!response.ok && response.status !== 404) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to delete sound effect clip");
  }

  return true;
}

// Default categories fallback (Freesound provider)
export const DEFAULT_CATEGORIES = [
  "ambient",
  "nature", 
  "music",
  "ui",
  "foley",
  "impact",
  "whoosh",
  "transition",
  "voice",
  "animal",
  "weather",
  "urban",
];

// Format duration for display (e.g., "1.5s" or "1:30")
export function formatDuration(seconds) {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// Calculate loop count needed to fill duration
export function calculateLoopCount(assetDuration, clipDuration) {
  if (!assetDuration || assetDuration <= 0) return 1;
  return Math.ceil(clipDuration / assetDuration);
}
